import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { Code, Cloud, Sparkles, Zap, ArrowRight, ChevronDown, Rocket, Check } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

// Particle component for background
const Particle = ({ x, y, size, delay, duration }: { x: string; y: string; size: number; delay: number; duration: number }) => (
  <motion.div
    className="absolute rounded-full bg-gradient-to-r from-blue-600/30 to-blue-400/30"
    style={{
      width: `${size}px`,
      height: `${size}px`,
      x,
      y,
    }}
    initial={{ opacity: 0 }}
    animate={{
      opacity: [0, 0.6, 0],
      scale: [0, 1.2, 0],
    }}
    transition={{
      duration: duration,
      delay: delay,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    }}
  />
);

const FloatingDevices = () => {
  // Generate subtle animation for floating effect
  const floatingAnimation: {
    y: number[];
    transition: {
      duration: number;
      repeat: number;
      repeatType: 'reverse' | 'loop' | 'mirror';
      ease: string;
      delay?: number;
    };
  } = {
    y: [0, -15, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'easeInOut'
    }
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto mt-32 lg:mt-0 lg:absolute lg:right-0 lg:top-48 lg:translate-y-0 px-4 sm:px-6">
      {/* Laptop */}
      <motion.div
        initial={{ x: 40, y: 40, opacity: 0, scale: 0.8 }}
        animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="relative z-10 w-full max-w-lg"
      >
        <motion.div
          className="relative"
          animate={{
            y: floatingAnimation.y,
            transition: floatingAnimation.transition
          }}
        >
          {/* Laptop Glow */}
          <div className="absolute -inset-4 bg-white/20 rounded-3xl blur-xl -z-10" />
          
          {/* Laptop Frame */}
          <div className="relative bg-gray-800 rounded-xl p-1 shadow-2xl shadow-blue-900/20 border border-gray-700">
            {/* Bezel */}
            <div className="h-6 bg-gray-700 rounded-t-lg border-b border-gray-600 flex items-center px-3">
              <div className="flex space-x-2">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              </div>
            </div>
            
            {/* Screen */}
            <div className="bg-white rounded-b-lg overflow-hidden">
              {/* Browser Chrome */}
              <div className="h-8 bg-gray-50 border-b border-gray-200 flex items-center px-2">
                <div className="flex space-x-1.5 mr-3">
                  <div className="w-3 h-3 rounded-full bg-gray-400 flex items-center justify-center">
                    <div className="w-1 h-1 rounded-full bg-white" />
                  </div>
                  <div className="w-3 h-3 rounded-full bg-gray-400 flex items-center justify-center">
                    <div className="w-1 h-1 rounded-full bg-white" />
                  </div>
                  <div className="w-3 h-3 rounded-full bg-gray-400 flex items-center justify-center">
                    <div className="w-1 h-1 rounded-full bg-white" />
                  </div>
                </div>
                
                {/* Tabs */}
                <div className="flex-1 flex">
                  <div className="max-w-xs w-full bg-white rounded-t-lg border border-gray-200 border-b-0 shadow-sm px-3 py-1.5 flex items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-700 mr-2" />
                    <div className="text-xs text-gray-800 truncate">Dashboard</div>
                  </div>
                  <div className="w-6 h-6 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center ml-1">
                    <div className="text-xs">+</div>
                  </div>
                </div>
                
                {/* Search */}
                <div className="ml-3 w-32 h-5 bg-white rounded-md border border-gray-200 shadow-sm flex items-center px-2">
                  <div className="w-2.5 h-2.5 rounded-full border border-gray-300 mr-1.5" />
                  <div className="h-1.5 w-16 bg-gray-200 rounded-full" />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex h-64">
                {/* Sidebar */}
                <div className="w-14 bg-gray-50 border-r border-gray-200 flex flex-col items-center py-3">
                  {['📊', '📈', '📑', '⚙️'].map((icon, i) => (
                    <div 
                      key={i} 
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg mb-3 ${i === 0 ? 'bg-blue-100 text-blue-700' : 'text-gray-800 hover:bg-gray-100'}`}>
                      {icon}
                    </div>
                  ))}
                </div>
                
                {/* Main Content */}
                <div className="flex-1 p-4 bg-white">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {[
                      { title: 'Visitors', value: '1,240', change: '+12%', color: 'bg-blue-500' },
                      { title: 'Revenue', value: '$8,420', change: '+8%', color: 'bg-green-500' },
                      { title: 'Orders', value: '284', change: '+5%', color: 'bg-purple-500' },
                      { title: 'Conversion', value: '3.2%', change: '-0.5%', color: 'bg-amber-500' }
                    ].map((stat, i) => (
                      <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{stat.title}</div>
                            <div className="text-lg font-semibold text-gray-800">{stat.value}</div>
                          </div>
                          <div className={`w-8 h-8 rounded-full ${stat.color.replace('500', '700')} flex items-center justify-center text-white text-xs`}>
                            {stat.change}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Chart Placeholder */}
                  <div className="h-24 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center text-gray-600 text-sm font-medium">
                    Performance Chart
                  </div>
                </div>
              </div>
            </div>
            
            {/* Keyboard Area */}
            <div className="h-2 bg-gray-900 rounded-b-lg mx-4" />
            <div className="h-1 bg-gray-800 rounded-b-lg mx-8" />
          </div>
        </motion.div>
      </motion.div>
      
      {/* Phone */}
      <motion.div
        initial={{ x: -40, y: 40, opacity: 0, scale: 0.8 }}
        animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="absolute top-20 left-1/2 -translate-x-1/2 lg:left-6 lg:translate-x-0 z-20 w-36"
      >
        <motion.div 
          className="relative"
          animate={{
            y: floatingAnimation.y.map(y => y * 1.5),
            transition: {
              ...floatingAnimation.transition,
              repeatType: 'reverse' as const,
              delay: 0.5
            }
          }}
        >
          {/* Phone Glow */}
          <div className="absolute -inset-2 bg-white/20 rounded-4xl blur-lg -z-10" />
          
          {/* Phone Frame */}
          <div className="relative bg-gray-800 rounded-[2.75rem] p-1.5 shadow-2xl shadow-blue-900/20 border border-gray-700 overflow-hidden">
            {/* Dynamic Island */}
            <div className="h-5 w-24 bg-black rounded-full absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center px-3">
              <div className="flex space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
              </div>
            </div>
            
            {/* Screen */}
            <div className="relative bg-white rounded-[1.75rem] overflow-hidden h-72">
              {/* Status Bar */}
              <div className="h-8 px-4 flex justify-between items-center">
                <div className="text-[10px] font-bold text-gray-800">9:41</div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-green-500 flex items-center justify-center">
                    <div className="w-1 h-1 rounded-full bg-white/80" />
                  </div>
                  <div className="w-3 h-3 rounded-full bg-blue-500 flex items-center justify-center">
                    <div className="w-1 h-1 rounded-full bg-white/80" />
                  </div>
                </div>
              </div>
              
              {/* App Grid */}
              <div className="p-3 bg-gray-50 h-full">
                {/* Widgets */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-gradient-to-br from-blue-100 to-white rounded-xl p-2 border border-gray-200">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-[8px] font-medium text-gray-600">Messages</div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    </div>
                    <div className="text-[10px] text-gray-800 font-medium">2 new</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-100 to-white rounded-xl p-2 border border-gray-200">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-[8px] font-medium text-gray-600">Calendar</div>
                      <div className="text-[8px] text-gray-500">Today</div>
                    </div>
                    <div className="text-[10px] text-gray-800 font-medium">3 events</div>
                  </div>
                </div>
                
                {/* App Icons */}
                <div className="grid grid-cols-4 gap-3 px-1 mb-3">
                  {['📱', '✉️', '📷', '🎵', '🗺️', '📅', '🛒', '⚙️'].map((icon, i) => (
                    <div key={i} className="w-6 h-6 rounded-xl bg-white shadow-sm flex items-center justify-center text-sm">
                      {icon}
                    </div>
                  ))}
                </div>
                
                {/* Featured App */}
                <div className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
                  <div className="flex items-center mb-2">
                    <div className="w-5 h-5 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-xs text-white mr-2">
                      Z
                    </div>
                    <div className="text-[10px] font-medium text-gray-800">Zell App</div>
                  </div>
                  <div className="h-2 w-full bg-gray-200 rounded-full mb-1"></div>
                  <div className="h-2 w-3/4 bg-gray-200 rounded-full"></div>
                </div>
                
                {/* Dock */}
                <div className="absolute bottom-2 left-2 right-2 h-10 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 flex justify-around items-center px-4">
                  {['📞', '✏️', '🔍', '👤'].map((icon, i) => (
                    <div key={i} className={`text-lg ${i === 1 ? 'text-blue-500' : 'text-gray-500'}`}>
                      {icon}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Side Button */}
            <div className="absolute top-1/2 -right-0.5 w-1 h-8 bg-gray-600 rounded-l-full" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const ScrollIndicator = () => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    animate={{ 
      opacity: [0, 1, 0],
      y: [10, 0, -10]
    }}
    transition={{ 
      duration: 2, 
      repeat: Infinity, 
      repeatType: "loop" 
    }}
    className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-muted-foreground/60"
  >
    <ChevronDown className="w-6 h-6 mb-1" />
    <span className="text-sm font-medium">Scroll to explore</span>
  </motion.div>
);

const AnimatedText = ({ text, className = "" }: { text: string; className?: string }) => {
  return (
    <span className="relative">
      {text.split('').map((char, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: i * 0.03,
          }}
          className={`inline-block ${className}`}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </span>
  );
};

const HeroSection = () => {
  const { openDrawer } = useQuoteDrawer();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
    layoutEffect: false
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 20]); // Minimal movement

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    const handleScroll = () => {
      // Your scroll logic here
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const stats = [
    { value: '100+', label: 'Projects', icon: '🚀', color: 'from-blue-900/50 to-blue-800/30' },
    { value: '50+', label: 'Clients', icon: '😊', color: 'from-blue-900/50 to-blue-800/30' },
    { value: '98%', label: 'Satisfaction', icon: '⭐', color: 'from-blue-900/50 to-blue-800/30' },
    { value: '24/7', label: 'Support', icon: '🛡️', color: 'from-blue-900/50 to-blue-800/30' },
  ];

  return (
    <section 
      ref={containerRef} 
      className="relative pt-12 md:pt-0 overflow-hidden bg-gradient-to-b from-background to-purple-50/30"
    >
      {/* Static background (no animations) */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-purple-50">
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden">
          <Particle x="10%" y="20%" size={20} delay={0} duration={12} />
          <Particle x="80%" y="70%" size={16} delay={2} duration={15} />
          <Particle x="30%" y="40%" size={24} delay={4} duration={18} />
        </div>
      </div>

      <div className="w-full relative z-10 py-20 lg:py-32">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center justify-center text-center lg:text-left">
            <div className="w-full flex flex-col items-center lg:items-start">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: isMobile ? 0.3 : 0.6 }}
                className="inline-flex items-center justify-center mb-6"
              >
                <motion.span 
                  className="h-2 w-2 bg-purple-400 rounded-full mr-2"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <span className="text-sm font-medium text-purple-800/80 flex items-center">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5 text-purple-500" />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-pink-500">
                    Innovating Since 2017
                  </span>
                  <ChevronDown className="w-4 h-4 ml-1.5 text-purple-700/60 group-hover:translate-y-0.5 transition-transform" />
                </span>
              </motion.div>

              {/* Main Heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-center mb-12 px-4"
              >
                <div className="relative">
                  <motion.div 
                    className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg blur opacity-20 group-hover:opacity-40 transition duration-300"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.2 }}
                  />
                  <motion.h1
                    className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-gray-950 bg-gradient-to-r from-white to-white/0 bg-clip-text"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    id="main-heading"
                  >
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600">
                      We Architect
                    </span>
                    <br />
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600">
                      Tomorrow's Tech
                    </span>
                  </motion.h1>
                </div>

                <motion.p
                  className="mt-6 text-lg md:text-xl text-gray-800 max-w-2xl mx-auto bg-gradient-to-r from-white to-white/0 bg-clip-text"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  Building innovative solutions that drive your business forward with cutting-edge technology and expert craftsmanship.
                </motion.p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="flex flex-col sm:flex-row justify-center gap-4"
              >
                <motion.div
                  className="inline-block"
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                >
                  <Button
                    asChild
                    size="lg"
                    className="group relative px-8 py-6 text-lg font-semibold rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300 overflow-hidden"
                  >
                    <Link href="/portfolio" className="relative z-10 flex items-center">
                      <span>Explore Our Work</span>
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </Link>
                  </Button>
                </motion.div>

                <motion.div
                  className="inline-block"
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                >
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={openDrawer}
                    className="group relative px-8 py-6 text-lg font-semibold rounded-xl border-2 border-purple-500/50 bg-white text-purple-700 hover:bg-purple-50 hover:text-purple-900 hover:border-purple-600 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-purple-500/20"
                  >
                    <span className="relative z-10 flex items-center">
                    Get Free Quote
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-100/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </Button>
                </motion.div>
              </motion.div>
              
              {/* Trust indicators */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-8 pt-6 border-t border-gray-200"
              >
                <p className="text-xs text-gray-500 text-center mb-4">Trusted by industry leaders</p>
                <div className="flex flex-wrap justify-center gap-3 px-4">
                  {[
                    { name: 'Microsoft', bg: 'bg-blue-100', text: 'text-blue-800' },
                    { name: 'Google', bg: 'bg-red-100', text: 'text-red-800' },
                    { name: 'Amazon', bg: 'bg-yellow-100', text: 'text-yellow-800' },
                    { name: 'Uber', bg: 'bg-green-100', text: 'text-green-800' },
                    { name: 'Netflix', bg: 'bg-purple-100', text: 'text-purple-800' },
                  ].map((brand) => (
                    <div 
                      key={brand.name}
                      className={`${brand.bg} ${brand.text} text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap`}
                    >
                      {brand.name}
                    </div>
                  ))}
                </div>
              </motion.div>

            </div>
            
            {/* Floating Devices */}
            <FloatingDevices />
          </div>
          
          {/* Scroll Indicator */}
          <ScrollIndicator />

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
